import { Typography } from '@mui/material';
import { useModalContext } from '@vxui/components';
import { Property } from 'csstype';
import { ReactNode, useCallback } from 'react';
import { ConfirmationDialog } from '../components';

export type DialogType = 'none' | 'delete' | 'confirm';

export type ConfirmationDialogArgs<T> = {
  onConfirm?: (obj: T) => void;
  messageFormatter: string | ((obj?: T) => ReactNode);
  titleFormatter?: string | ((obj?: T) => string);
  type?: DialogType | ((obj: T) => DialogType);
  modalId?: string;
  isDelete?: boolean;
  wordBreak?: Property.WordBreak;
};

export const useConfirmationDialog = <T,>({
  onConfirm,
  messageFormatter,
  titleFormatter,
  type,
  modalId = 'confirmationDialog',
  isDelete,
  wordBreak = 'break-all',
}: ConfirmationDialogArgs<T>) => {
  const { showModal, hideModal } = useModalContext();

  const closeConfirmationDialog = useCallback(() => {
    hideModal(modalId);
  }, [hideModal, modalId]);

  const onConfirmHandler = useCallback(
    (obj: T) => {
      onConfirm && onConfirm(obj);
      closeConfirmationDialog();
    },
    [onConfirm, closeConfirmationDialog],
  );

  const dialogType = useCallback(
    (obj?: T) =>
      type ? (type instanceof Function ? (obj ? type(obj) : 'none') : type) : isDelete ? 'delete' : 'confirm',
    [type, isDelete],
  );

  const title = useCallback(
    (obj?: T) => {
      if (!titleFormatter) return '';
      if (typeof titleFormatter === 'string') return titleFormatter;
      else return titleFormatter(obj);
    },
    [titleFormatter],
  );

  const message = useCallback(
    (obj?: T) => {
      if (!messageFormatter) return '';
      if (typeof messageFormatter === 'string') return messageFormatter;
      else return messageFormatter(obj);
    },
    [messageFormatter],
  );

  const openConfirmationDialog = useCallback(
    (obj: T) =>
      showModal(
        modalId,
        <ConfirmationDialog
          open
          title={title(obj)}
          closeHandler={closeConfirmationDialog}
          confirmHandler={() => onConfirmHandler(obj)}
          action={dialogType(obj)}
        >
          <Typography variant="p16" sx={{ wordBreak }}>
            {message(obj)}
          </Typography>
        </ConfirmationDialog>,
      ),
    [closeConfirmationDialog, dialogType, message, modalId, onConfirmHandler, showModal, title, wordBreak],
  );

  return {
    openConfirmationDialog,
    closeConfirmationDialog,
  };
};
